'use client';

import { c as _c } from "react/compiler-runtime";
import { type JSX, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Download01Icon, LinkForwardIconSolid, Loading03Icon, RotateClockwiseIcon } from '@/components/ui/icon/glyphs';
import { useUserNetwork } from '@/lib/network/client';
import { resolveCanonicalOrigin } from '@/lib/seo';
import { getOgImageUrl } from '@/lib/share/og';
import { logger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
import { normalizeShareSurface, type ShareSurfaceInput } from './events';
interface ShareOgPreviewProps {
  surface: ShareSurfaceInput;
  /** Alt text for the image. Caller-supplied. */
  alt?: string;
  version?: string | undefined;
}
const IS_DEV: boolean = process.env.NODE_ENV === 'development';

/**
 * Shows the actual OG image at the top of the share modal so users
 * see what their share will unfurl as.
 *
 * Reserves the preview area with a loader on first load, then keeps the image
 * visible until its replacement is ready. Failed refreshes preserve the preview.
 *
 * Overlays a small toolbar at the top-right of the preview:
 *   - Download (all users) — saves the PNG locally with a descriptive
 *     filename so a user can pull the card into Figma / Slack / a doc.
 *   - Refresh (dev only) — bumps a cache-bust counter and re-fetches the
 *     PNG. Useful while iterating on a card design without redeploying.
 *   - Preview PNG (dev only) — opens the raw PNG URL in a new tab so the
 *     byte-identical artifact crawlers will fetch can be inspected at full size.
 * The dev-only buttons are statically tree-shaken in production builds.
 */

function deriveDownloadFilename(url: string): string {
  try {
    const u = new URL(url, resolveCanonicalOrigin());
    const segments = u.pathname.split('/').filter(s => s.length > 0);
    const last = segments[segments.length - 1] ?? 'og';
    const surface = segments[segments.length - 2] ?? 'morfi';
    return `morfi-${surface}-${last}.png`;
  } catch {
    // SILENT-CATCH: download filename is cosmetic; on a malformed URL we
    // fall back to a generic name rather than blocking the download.
    return 'morfi-og.png';
  }
}
export function ShareOgPreview(props) {
  const $ = _c(33);
  const surface = normalizeShareSurface(props.surface);
  const {
    network
  } = useUserNetwork();
  const useDemoMode = network !== "mainnet" && surface.kind !== "support_mainnet";
  const baseUrl = getOgImageUrl(surface, {
    demoMode: useDemoMode
  });
  const alt = props.alt ?? "Share preview";
  const [failedUrl, setFailedUrl] = useState(null);
  const [loadedUrl, setLoadedUrl] = useState(null);
  const [bust, setBust] = useState(() => surface.kind === "winner" ? Date.now() : 0);
  const previewUrl = new URL(baseUrl);
  if (surface.kind === "winner") {
    previewUrl.searchParams.set("preview", "1");
  }
  if (bust !== 0) {
    previewUrl.searchParams.set("v", String(bust));
  }
  if (props.version !== undefined) {
    previewUrl.searchParams.set("revision", props.version);
  }
  const url = previewUrl.toString();
  let t0;
  if ($[0] !== setBust) {
    t0 = function refresh() {
      setBust(Date.now());
    };
    $[0] = setBust;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const refresh = t0;
  let t1;
  if ($[2] !== loadedUrl || $[3] !== url) {
    t1 = loadedUrl === null || loadedUrl === url ? [url] : [loadedUrl, url];
    $[2] = loadedUrl;
    $[3] = url;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const imageUrls = t1;
  const downloadUrl = loadedUrl ?? url;
  let t2;
  if ($[5] !== failedUrl || $[6] !== loadedUrl || $[7] !== url) {
    t2 = loadedUrl === null && failedUrl !== url && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><Icon icon={Loading03Icon} className="size-10 animate-spin text-foreground-secondary motion-reduce:animate-none" /></div>;
    $[5] = failedUrl;
    $[6] = loadedUrl;
    $[7] = url;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== alt || $[10] !== failedUrl || $[11] !== imageUrls || $[12] !== loadedUrl || $[13] !== setFailedUrl || $[14] !== setLoadedUrl || $[15] !== surface) {
    t3 = imageUrls.map(imageUrl => imageUrl === failedUrl ? null : <img key={imageUrl} src={imageUrl} alt={alt} aria-hidden={imageUrl !== loadedUrl} className={cn("block h-full w-full object-cover", imageUrl !== loadedUrl && "absolute inset-0 opacity-0")} loading="eager" onLoad={() => setLoadedUrl(imageUrl)} onError={() => {
      logger.warn("share.preview.load_failed", {
        kind: surface.kind
      });
      setFailedUrl(imageUrl);
    }} />);
    $[9] = alt;
    $[10] = failedUrl;
    $[11] = imageUrls;
    $[12] = loadedUrl;
    $[13] = setFailedUrl;
    $[14] = setLoadedUrl;
    $[15] = surface;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== downloadUrl || $[18] !== refresh) {
    t4 = IS_DEV && <><button type="button" onClick={refresh} className="flex items-center gap-1 rounded-full border border-white/20 bg-surface-scrim-default px-3 py-1.5 text-caption font-medium text-white backdrop-blur hover:bg-surface-scrim-strong" title="Refresh preview (Dev only)"><Icon icon={RotateClockwiseIcon} className="size-2.5" />Refresh</button><a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 rounded-full border border-white/20 bg-surface-scrim-default px-3 py-1.5 text-caption font-medium text-white backdrop-blur hover:bg-surface-scrim-strong" title="Open PNG in a new tab (Dev only)"><Icon icon={LinkForwardIconSolid} className="size-2.5" />Preview</a></>;
    $[17] = downloadUrl;
    $[18] = refresh;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== downloadUrl) {
    t5 = deriveDownloadFilename(downloadUrl);
    $[20] = downloadUrl;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={Download01Icon} className="size-3.5" />;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== downloadUrl || $[24] !== t5) {
    t7 = <a href={downloadUrl} download={t5} className="flex items-center rounded-full border border-white/20 bg-surface-scrim-default p-1.5 text-caption font-medium text-white backdrop-blur hover:bg-surface-scrim-strong" title="Download PNG" aria-label="Download PNG">{t6}</a>;
    $[23] = downloadUrl;
    $[24] = t5;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t4 || $[27] !== t7) {
    t8 = <div className="absolute right-2 top-2 flex items-center gap-2">{t4}{t7}</div>;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t2 || $[30] !== t3 || $[31] !== t8) {
    t9 = <div className="relative aspect-[1200/630] w-full overflow-hidden rounded-card border bg-background-tertiary">{t2}{t3}{t8}</div>;
    $[29] = t2;
    $[30] = t3;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}